import { useEffect, useState } from "react";

import { createFileRoute } from "@tanstack/react-router";

import { useNews, useNewsSources } from "../api/hooks";
import { agoLabel, dayLabel, sinceLabel } from "../lib/relativeTime";

import styles from "./sector-news.module.scss";

// The tag is a slug on the source row and doubles as the ?tag= filter value, so
// it stays lowercase in the data and gets capitalised only for display. The
// exceptions are the ones a capitalised first letter gets wrong: "Bng" is not a
// word, and nobody writes it that way.
const tagLabels: Record<string, string> = { bng: "BNG" };

function tagLabel(tag: string): string {
  return tagLabels[tag] ?? tag.charAt(0).toUpperCase() + tag.slice(1);
}

// The feeds are RSS, so there is no favicon to fetch; a monogram is what makes a
// repeated source recognisable down a column of mixed sources. Two characters
// either way - a lone "G" for gov.uk is too thin a mark to recognise, so a
// single-word name gives up its first two letters instead.
function initials(sourceName: string): string {
  const words = sourceName.split(/\s+/).filter((word) => word.length > 0);

  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  return (words[0][0] + words[1][0]).toUpperCase();
}

// The URL is feed-controlled data: the ingest deliberately stores it verbatim,
// including empty (a guid-only item is still a headline). An empty href would
// link back to Lodge itself, and a javascript: URL would execute in the Lodge
// origin, so only http(s) becomes an anchor - anything else renders as text.
function isSafeArticleUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

// A source that has quietly started failing is otherwise invisible on this page,
// because the only symptom is fewer headlines. Rendered only when something is wrong,
// so a healthy feed shows nothing.
//
// Overdue matters as much as erroring: a failed poll still records lastPolledAt,
// so a stopped schedule or worker produces no errors and no nulls - silence is
// its only signature. Three intervals of slack absorbs scheduling jitter without
// sitting on a real outage for long. Inactive sources are deliberately excluded
// here (switched off is not unhealthy); the endpoint still lists them so "why is
// this feed missing" stays answerable from the API.
function SourceHealth() {
  const { data: sources } = useNewsSources();
  const now = Date.now();

  const attention = (sources ?? [])
    .filter((source) => source.active)
    .map((source) => {
      if (source.lastPolledAt === null) return `${source.name} (never polled)`;
      if (source.lastError !== "") return `${source.name} (${source.lastError})`;

      const minutesSincePoll = Math.round(
        (now - new Date(source.lastPolledAt).getTime()) / 60_000,
      );
      if (minutesSincePoll > source.pollIntervalMinutes * 3) {
        return `${source.name} (overdue: last polled ${agoLabel(minutesSincePoll)})`;
      }

      return null;
    })
    .filter((line): line is string => line !== null);

  if (attention.length === 0) return null;

  return (
    <div className="alert-banner">
      <span className="alert-text">
        {attention.length === 1
          ? "1 source needs attention:"
          : `${attention.length} sources need attention:`}{" "}
        {attention.join(", ")}
      </span>
    </div>
  );
}

interface FeedItem {
  id: string;
  title: string;
  url: string;
  summary: string;
  publishedAt: string;
  sourceName: string;
  sourceTag: string;
}

function NewsCard({ item }: { item: FeedItem }) {
  // Relative time is what a triage page is read by, but several feeds republish
  // the same story within an hour, so the exact time has to stay reachable: the
  // element carries the machine-readable timestamp and the clock time on hover.
  const publishedExactly = new Date(item.publishedAt).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <article className={styles.card}>
      <div className={styles.cardHead}>
        {/* The monogram restates the source name two elements later, so it is
            decoration to a screen reader rather than a second announcement. */}
        <span className={styles.avatar} aria-hidden="true">
          {initials(item.sourceName)}
        </span>
        <span className={styles.cardMeta}>
          {item.sourceTag && (
            <span className={styles.tag}>{tagLabel(item.sourceTag)}</span>
          )}
          <span className={styles.metaText}>
            {item.sourceName} ·{" "}
            <time dateTime={item.publishedAt} title={publishedExactly}>
              {sinceLabel(item.publishedAt)}
            </time>
          </span>
        </span>
      </div>

      <h3 className={styles.cardTitle}>
        {isSafeArticleUrl(item.url) ? (
          <a href={item.url} target="_blank" rel="noreferrer">
            {item.title}
          </a>
        ) : (
          item.title
        )}
      </h3>

      {item.summary !== "" && (
        <p className={styles.cardSummary}>{item.summary}</p>
      )}
    </article>
  );
}

// Counts what is on screen, not what exists: the feed pages, so a total per
// source would need an endpoint that does not exist. The heading says "in view"
// for that reason, and the numbers move as more pages load.
function SourcesInView({ items }: { items: FeedItem[] }) {
  const counts = new Map<string, number>();
  for (const item of items) {
    counts.set(item.sourceName, (counts.get(item.sourceName) ?? 0) + 1);
  }

  const rows = Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  if (rows.length === 0) return null;

  return (
    <div className={styles.railCard}>
      <div className={styles.railTitle}>Sources in view</div>
      <div className={styles.railRows}>
        {rows.map((row) => (
          <div key={row.name} className={styles.railRow}>
            <span className={styles.railLabel} title={row.name}>
              {row.name}
            </span>
            <span className={styles.railCount}>{row.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeedsCard() {
  const { data: sources } = useNewsSources();

  if (!sources || sources.length === 0) return null;

  const polling = sources.filter((source) => source.active);
  // Tags of the sources actually polling, not of every source on file: the
  // sentence's subject is "polling", so naming a switched-off source's tag in it
  // says something untrue.
  const tags = Array.from(new Set(polling.map((source) => source.tag)))
    .sort()
    .map(tagLabel);

  return (
    <div className={styles.railCard}>
      <div className={styles.railTitle}>Feeds</div>
      <div className={styles.railBody}>
        {polling.length} of {sources.length}{" "}
        {sources.length === 1 ? "feed" : "feeds"} polling
        {tags.length > 0 && <>: {tags.join(", ")}.</>}
      </div>
    </div>
  );
}

function SectorNews() {
  const [tag, setTag] = useState<string | undefined>(undefined);
  const { data: sources } = useNewsSources();
  const {
    data,
    isPending,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useNews(tag);

  const items = data?.pages.flatMap((page) => page.items) ?? [];
  // The API counts the whole filtered feed, not the loaded pages, so this is a
  // real total rather than a running tally.
  const total = data?.pages[0]?.count;

  // Tags accumulate across everything seen rather than being derived from the
  // current page: deriving from a filtered page would collapse the bar to the
  // selected tag and force a round trip through All to change filters.
  const [knownTags, setKnownTags] = useState<string[]>([]);
  useEffect(() => {
    if (items.length === 0) return;

    setKnownTags((previous) => {
      const merged = new Set(previous);
      for (const item of items) {
        if (item.sourceTag) merged.add(item.sourceTag);
      }
      if (merged.size === previous.length) return previous;
      return Array.from(merged).sort();
    });
    // A stable key: the effect only needs to run when the visible items change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((item) => item.id).join("|")]);

  // The sources endpoint is the authoritative tag set, so a tag whose source
  // publishes rarely gets a pill on the first render rather than only once one
  // of its items happens to load - the rail names those tags either way, and a
  // strip that omits one the rail lists reads as broken. Only polling sources
  // contribute: a switched-off source's tag would filter to nothing unless its
  // older items are still in the feed, which is exactly what the item-derived
  // set below adds.
  const filterTags = Array.from(
    new Set([
      ...(sources ?? [])
        .filter((source) => source.active)
        .map((source) => source.tag),
      ...knownTags,
    ]),
  )
    .filter((availableTag) => availableTag !== "")
    .sort();

  // Grouped up front rather than tracked while mapping, because the day divider
  // states how many items sit under it.
  const days: { label: string; items: FeedItem[] }[] = [];
  for (const item of items) {
    const label = dayLabel(item.publishedAt);
    const current = days[days.length - 1];
    if (current && current.label === label) {
      current.items.push(item);
    } else {
      days.push({ label, items: [item] });
    }
  }

  return (
    <>
      <div className={styles.header}>
        <div>
          <div className="page-title">Sector news</div>
          <div className="page-subtitle">
            UK rewilding and nature restoration, newest first
          </div>
        </div>
        {total !== undefined && (
          <div className={styles.headerCount}>
            {total.toLocaleString("en-GB")} {total === 1 ? "item" : "items"}
          </div>
        )}
      </div>

      <SourceHealth />

      {/* Labelled as a group: the strip lost the panel title it used to sit
          under, and a row of toggles announced without it says what it is
          pressed but not what it filters. */}
      {filterTags.length > 0 && (
        <div className={styles.filters} role="group" aria-label="Filter by tag">
          <button
            type="button"
            className={styles.pill}
            aria-pressed={tag === undefined}
            onClick={() => setTag(undefined)}
          >
            Everything
          </button>
          {filterTags.map((availableTag) => (
            <button
              key={availableTag}
              type="button"
              className={styles.pill}
              aria-pressed={tag === availableTag}
              onClick={() => setTag(availableTag)}
            >
              {tagLabel(availableTag)}
            </button>
          ))}
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.feed}>
          {/* Announced, because each of these replaces the feed itself: without
              it the page just goes quiet for anyone not watching the column. */}
          {isPending && (
            <div className={styles.state} role="status">
              Loading the feed…
            </div>
          )}

          {/* alert, not status: this one has replaced the entire feed, which is
              worth interrupting for. */}
          {isError && (
            <div className={styles.state} role="alert">
              The feed could not be loaded. The ingest worker may not have run
              yet.
            </div>
          )}

          {!isPending && !isError && items.length === 0 && (
            <div className={styles.state} role="status">
              Nothing here yet. The poll runs every fifteen minutes.
            </div>
          )}

          {/* Keyed on the first item, not the label: dayLabel omits the year, so
              a feed reaching back past twelve months repeats a label. */}
          {days.map((day) => (
            <div key={day.items[0].id} className={styles.day}>
              <div className={styles.divider}>
                <span className={styles.dividerLabel}>{day.label}</span>
                <span className={styles.dividerRule} />
                <span className={styles.dividerCount}>
                  {day.items.length} shown
                </span>
              </div>
              {day.items.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          ))}

          {hasNextPage && (
            <div className={styles.feedFoot}>
              {/* How far down the feed you are, because the button fetches one
                  more page rather than the remainder: "load the rest" would
                  promise the whole archive. */}
              {total !== undefined && (
                <span className={styles.feedFootNote}>
                  {items.length.toLocaleString("en-GB")} of{" "}
                  {total.toLocaleString("en-GB")} shown, oldest last
                </span>
              )}
              <button
                type="button"
                className={styles.loadMore}
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>

        <div className={styles.rail}>
          <SourcesInView items={items} />
          <FeedsCard />
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/sector-news")({
  component: SectorNews,
});
